'use client';

import React from 'react';
import { Loader2 } from 'lucide-react';

import { ChapterItem } from './chapter-item';
import { Chapter } from '@/features/fanfic-page/types/fanfic';
import { cn } from '@/lib/utils';

interface VirtualizedChapterListProps {
  chapters: Chapter[];
  selectedChapterId: string | null;
  onMarkAsRead?: (chapterId: number) => void;
  onOpenFullscreen?: (chapterId: string) => void;
  progress: { chapterIndex: number; progress: number }[];
  // Pagination props from API
  hasMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  onLoadAll?: () => void | Promise<void>;
  totalDocs?: number;
  isLoadingMore?: boolean;
  isRating18: boolean;
  workId: string;
  workPathPrefix: '/fanfics' | '/originals';
  /**
   * When set, only the first N chapters are visible; the rest stay in the DOM
   * (hidden) so SSR/SEO links remain available without extra requests.
   */
  visibleCount?: number;
}

export const VirtualizedChapterList = React.memo(
  ({
    chapters,
    selectedChapterId,
    onMarkAsRead,
    onOpenFullscreen,
    progress,
    hasMore = false,
    onLoadMore,
    onLoadAll,
    totalDocs = 0,
    isLoadingMore = false,
    isRating18 = false,
    workId,
    workPathPrefix,
    visibleCount,
  }: VirtualizedChapterListProps) => {
    const renderedCount = visibleCount ?? chapters.length;
    const remainingCount =
      visibleCount != null ? chapters.length - renderedCount : totalDocs - chapters.length;
    const canRevealMore = visibleCount != null ? renderedCount < chapters.length : hasMore;

    return (
      <div className="space-y-3">
        {chapters.map((chapter, index) => (
          <div key={chapter.id} className={cn(index >= renderedCount && 'hidden')}>
            <ChapterItem
              chapter={chapter}
              isSelected={
                progress.find((item) => item.chapterIndex === index)?.progress === 100 ||
                selectedChapterId === chapter.id
              }
              isRead={progress.find((item) => item.chapterIndex === index)?.progress === 100}
              onMarkAsRead={() => onMarkAsRead?.(index)}
              progress={progress.find((item) => item.chapterIndex === index)?.progress || 0}
              wordCount={chapter.wordCount || 0}
              onOpenFullscreen={onOpenFullscreen}
              isRating18={isRating18}
              workId={workId}
              workPathPrefix={workPathPrefix}
            />
          </div>
        ))}
        {isLoadingMore && (
          <div className="flex justify-center py-6">
            <Loader2 className="w-8 h-8 text-[#505CFF] animate-spin" />
          </div>
        )}
        {canRevealMore && !isLoadingMore && (
          <div className="pt-4 flex gap-x-2">
            <button
              type="button"
              onClick={onLoadMore}
              className="text-sm text-[#1B1D22] border border-[#A7ADC5] rounded-[12px] border-solid !h-[46px] w-full hover:bg-[#E8E9ED] transition-colors"
            >
              Показать еще 10
            </button>
            <button
              type="button"
              onClick={onLoadAll}
              className="text-sm text-white bg-[#1B1D22] rounded-[12px] !h-[46px] w-full hover:opacity-80 transition-opacity"
            >
              Показать все ({remainingCount})
            </button>
          </div>
        )}
      </div>
    );
  },
);
